import { z } from "zod";

/**
 * Date-range filters travel in the URL as local calendar days (`2026-10-14`),
 * so a shared link reads the same everywhere, and become instants only when
 * a list is queried.
 */
export const DayKey = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD.");

export interface DayRangeSearch {
  from?: string | undefined;
  to?: string | undefined;
}

/** `YYYY-MM-DD` of a local day. */
export function toDayKey(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local midnight of a `YYYY-MM-DD` day; `null` for anything that isn't one. */
export function fromDayKey(key: string | undefined): Date | null {
  if (!key || !DayKey.safeParse(key).success) return null;
  const [year, month, day] = key.split("-").map(Number) as [number, number, number];
  const date = new Date(year, month - 1, day);
  return toDayKey(date) === key ? date : null;
}

/** The picker's value from the URL: both ends, or nothing. */
export function dayRangeFromSearch(search: DayRangeSearch): { from: Date; to: Date } | null {
  const from = fromDayKey(search.from);
  const to = fromDayKey(search.to);
  return from && to && from <= to ? { from, to } : null;
}

/** The `[from, to)` instants covering the range's whole local days, as list queries take them. */
export function dayRangeBounds(range: { from: Date; to: Date } | null): { from?: string; to?: string } {
  if (!range) return {};
  const end = new Date(range.to.getFullYear(), range.to.getMonth(), range.to.getDate() + 1);
  return { from: range.from.toISOString(), to: end.toISOString() };
}
